import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { FaSignOutAlt, FaCogs } from "react-icons/fa";
import { getGlobalLogo } from "services/settings.service";
import { BarChart3 } from "lucide-react";
import Avatar from "components/ui/avatar";
import { getProfileImage } from "utils/helpers";
import logoHeader from "../../assets/img/logo_header.png";
import "./styles.scss";

interface HeaderGestorProps {
  className?: string;
}

export function HeaderGestor({ className = "" }: HeaderGestorProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [customLogoUrl, setCustomLogoUrl] = useState<string>("");

  useEffect(() => {
    loadCustomLogo();
  }, []);

  const loadCustomLogo = async () => {
    try {
      const response = await getGlobalLogo();
      if (response && response.logoUrl) {
        setCustomLogoUrl(response.logoUrl);
      }
    } catch (error) {
      console.error("Erro ao carregar logo customizada:", error);
    }
  };

  const handleLogout = () => {
    signOut();
    navigate("/");
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Função para verificar se a rota está ativa
  const isActiveRoute = (path: string) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header className={`app-header header-gestor ${className}`}>
      <div className="header-container">
        {/* Logo */}
        <div className="header-logo flex items-center gap-3">
          <Link to="/" onClick={closeDropdown} className="relative">
            <img
              src={logoHeader}
              alt="Rankifier"
              className="h-10 w-auto object-contain"
            />
            <span className="gestor-badge">Gestor</span>
          </Link>
          {customLogoUrl && (
            <img
              src={customLogoUrl}
              alt="Logo customizada"
              className="h-10 w-auto max-w-[120px] object-contain border-l border-white/20 pl-3"
            />
          )}
        </div>

        {user && (
          <nav className="header-nav">
            <Link
              to="/gestao/exibicao"
              className={`nav-item ${
                isActiveRoute("/gestao/exibicao") ? "nav-item-active" : ""
              }`}
              onClick={closeDropdown}
            >
              <div className="nav-icon">
                <BarChart3 size={20} />
              </div>
              <span>Modo de Exibição</span>
            </Link>

            {/* Gestão */}
            <Link
              to="/gestao"
              className={`nav-item ${
                isActiveRoute("/gestao") && !isActiveRoute("/gestao/exibicao")
                  ? "nav-item-active"
                  : ""
              }`}
              onClick={closeDropdown}
            >
              <div className="nav-icon">
                <FaCogs />
              </div>
              <span>Gestão</span>
            </Link>

            {/* User Avatar com Dropdown */}
            <div className="nav-dropdown">
              <div
                className="nav-item user-avatar gestor-avatar"
                onClick={toggleDropdown}
              >
                <Avatar
                  src={getProfileImage(user)}
                  name={user?.name}
                  size="md"
                  className="header-avatar"
                />
              </div>

              {isDropdownOpen && (
                <div className="dropdown-menu">
                  <button onClick={handleLogout}>
                    <FaSignOutAlt />
                    Sair
                  </button>
                </div>
              )}
            </div>
          </nav>
        )}

        {/* Login link for non-authenticated users */}
        {!user && (
          <div className="header-auth">
            <Link to="/login" className="login-link">
              Entrar como Gestor
            </Link>
          </div>
        )}
      </div>

      {/* Overlay for closing dropdown */}
      {isDropdownOpen && (
        <div className="dropdown-overlay" onClick={closeDropdown} />
      )}
    </header>
  );
}
